import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { ProductZoomModalComponent } from '../../shared/ui/product-zoom-modal/product-zoom-modal.component';
import type { AddToCartEvent, Product } from '../../shared/models/product.model';
import type { Category } from '../../shared/models/category.model';
import { cartErrorMessage } from '../../shared/utils/cart.utils';
import { CategoryService } from '../../shared/services/category.service';
import { CartService } from '../../shared/services/cart.service';
import { CartDrawerService } from '../../shared/services/cart-drawer.service';
import { PrepurchaseModalService } from '../../shared/services/prepurchase-modal.service';
import { StockAlertModalService } from '../../shared/services/stock-alert-modal.service';
import { StockAlertStore } from '../../shared/services/stock-alert.store';
import { ToastService } from '../../shared/services/toast.service';
import { SeoService } from '../../shared/services/seo.service';
import { truncate } from '../../shared/utils/seo.utils';
import { deriveProductCategorySlug } from '../catalog/data/catalog-mappers';
import { GameSectionService } from './data/game-section.service';
import { publicGameSectionToUi } from './data/game-section.mappers';
import { GAME_NAV_SECTIONS, GAME_SECTION_COPY } from './constants/game-section.constants';
import type { GameNavSection, GameSection } from './models/game-section.model';
import { GameHeroComponent } from './blocks/game-hero/game-hero.component';
import { GameIntroComponent } from './blocks/game-intro/game-intro.component';
import { StarterGuideComponent } from './blocks/starter-guide/starter-guide.component';
import { GameCommunityComponent } from './blocks/game-community/game-community.component';
import { GameBlogPostsComponent } from './blocks/game-blog-posts/game-blog-posts.component';
import { GameSectionNavComponent } from './components/game-section-nav/game-section-nav.component';

/** Estado de carga de la landing. `not-found` cubre slug inexistente, sección despublicada y borrada. */
type GameSectionStatus = 'loading' | 'ready' | 'not-found' | 'error';

@Component({
  selector: 'app-game-section-page',
  imports: [
    GameHeroComponent,
    GameIntroComponent,
    StarterGuideComponent,
    GameCommunityComponent,
    GameBlogPostsComponent,
    GameSectionNavComponent,
    ProductZoomModalComponent,
  ],
  templateUrl: './game-section-page.component.html',
  styleUrl: './game-section-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--game-accent-color]': 'game()?.accentColor',
    '[style.--game-accent-color-light]': 'game()?.accentColorLight',
  },
})
export class GameSectionPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly categoryService = inject(CategoryService);
  private readonly gameSectionService = inject(GameSectionService);
  private readonly seo = inject(SeoService);
  private readonly cartService = inject(CartService);
  private readonly cartDrawerService = inject(CartDrawerService);
  private readonly prepurchaseModalService = inject(PrepurchaseModalService);
  private readonly stockAlertModal = inject(StockAlertModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);

  // Cache compartido para resolver la categoría canónica al navegar a la ficha,
  // tanto desde la card como desde el quickview. Sin esto, ni el clic en la
  // tarjeta ni "Ver ficha completa" saben a qué /tienda/:slug ir.
  private readonly rootsSig = toSignal(
    this.categoryService.getRootCategoriesCached(),
    { initialValue: [] as Category[] },
  );

  protected readonly copy = GAME_SECTION_COPY;

  protected readonly game = signal<GameSection | null>(null);
  protected readonly status = signal<GameSectionStatus>('loading');
  protected readonly activeSection = signal<string>('que-es');
  protected readonly zoomProduct = signal<Product | null>(null);
  /** Campana del quickview: misma fuente que la card (`StockAlertStore`). */
  protected readonly zoomNotifyActive = computed(() =>
    this.stockAlertStore.hasAlert(this.zoomProduct()?.id),
  );

  protected readonly skeletonParagraphs: readonly number[] = [0, 1, 2];
  protected readonly skeletonProducts: readonly number[] = [0, 1, 2, 3];

  /**
   * Índice lateral filtrado por lo que la página realmente pinta: si el
   * backend omitió un bloque, su entrada de nav tampoco aparece (llevaría a un
   * ancla que no existe).
   */
  protected readonly navSections = computed<GameNavSection[]>(() => {
    const section = this.game();
    if (!section) return [];
    return GAME_NAV_SECTIONS.filter((nav) => this.isSectionRendered(section, nav.id));
  });

  private scrollSpyObserver: IntersectionObserver | null = null;

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('gameSlug')),
        distinctUntilChanged(),
        tap(() => {
          this.status.set('loading');
          this.game.set(null);
        }),
        switchMap((slug) => {
          if (!slug) return of({ kind: 'not-found' as const });
          return this.gameSectionService.getBySlug(slug).pipe(
            map((payload) =>
              payload
                ? { kind: 'ready' as const, section: publicGameSectionToUi(payload) }
                : { kind: 'not-found' as const },
            ),
            // Un fallo de red no es un 404: el servicio ya tradujo el 404 a
            // `null`, así que lo que llegue aquí es un problema real y merece
            // su propio copy ("intenta de nuevo") en vez de "no encontrado".
            catchError(() => of({ kind: 'error' as const })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        if (result.kind !== 'ready') {
          this.game.set(null);
          this.status.set(result.kind);
          this.applyNotFoundSeo();
          return;
        }

        this.game.set(result.section);
        this.status.set('ready');
        this.activeSection.set(this.navSections()[0]?.id ?? 'que-es');
        this.applySeo(result.section);

        // El scroll-spy se re-arma con cada sección porque los `<section>` no
        // existen hasta que llegan los datos: montarlo una vez en el
        // constructor no encontraría ningún elemento que observar.
        afterNextRender(() => this.setupScrollSpy(), { injector: this.injector });
      });

    this.destroyRef.onDestroy(() => this.scrollSpyObserver?.disconnect());
  }

  protected scrollToSection(sectionId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const el = document.getElementById(sectionId);
    if (!el) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth', block: 'start' });
  }

  protected onZoomOpen(product: Product): void {
    this.zoomProduct.set(product);
  }

  protected onZoomClose(): void {
    this.zoomProduct.set(null);
  }

  // ── Carrito ──────────────────────────────────────────────────────────────
  protected onAddToCart(event: AddToCartEvent): void {
    this.cartService
      .addLine(Number(event.product.id), event.quantity, event.product)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.cartDrawerService.open(),
        error: (err) => {
          // `null` = bloqueo de Acceso VIP: el diálogo ya lo explica.
          const message = cartErrorMessage(err, 'No pudimos agregar el producto al carrito.');
          if (message) this.toastService.show({ type: 'error', message });
        },
      });
  }

  /**
   * "Agregar" desde el quickview. Reutiliza el handler de la card y cierra el
   * modal sin esperar al response: `onAddToCart` abre el drawer del carrito, y
   * si el quickview sigue abierto el drawer sale por debajo y el usuario no ve
   * lo que acaba de pasar. Mismo criterio que `catalog-page.onZoomAddToCart`.
   */
  protected onZoomAddToCart(event: AddToCartEvent): void {
    this.onAddToCart(event);
    this.zoomProduct.set(null);
  }

  // ── Precompra ────────────────────────────────────────────────────────────
  /**
   * "Reservar" desde una card de la guía. No pasa por el carrito: abre el
   * `PrepurchaseModalService`, flujo paralelo que no toca `CartService`.
   */
  protected onReserve(product: Product): void {
    this.prepurchaseModalService.open(product);
  }

  /** "Reservar" desde el quickview: cierra el zoom y abre el modal con su qty. */
  protected onZoomReserve(event: AddToCartEvent): void {
    this.zoomProduct.set(null);
    this.prepurchaseModalService.open(event.product, event.quantity);
  }

  // ── Aviso de stock ───────────────────────────────────────────────────────
  // El starter guide muestra a propósito productos agotados (son la
  // recomendación editorial correcta): la card ya pinta "Agotado" y la campana,
  // y aquí se le da destino a esos dos outputs.
  protected onNotify(product: Product): void {
    this.stockAlertModal.open(product);
  }

  protected onNotifyCancel(product: Product): void {
    this.stockAlertModal.cancel(product);
  }

  // ── Navegación a la ficha ────────────────────────────────────────────────
  /** Clic en zona no interactiva de la card (texto, precio, hueco). */
  protected onCardClick(product: Product): void {
    this.navigateToProduct(product);
  }

  /** "Ver ficha completa" del quickview → cierra y navega al mismo destino. */
  protected onZoomViewDetails(product: Product): void {
    this.zoomProduct.set(null);
    this.navigateToProduct(product);
  }

  /**
   * La landing no vive bajo `/tienda/:category`, así que el slug de categoría
   * solo puede salir del árbol cacheado. Si el producto no trae slug o la
   * categoría no resuelve, no navegamos: mejor no moverse que llevar a un 404.
   */
  private navigateToProduct(product: Product): void {
    if (!product.slug) return;
    const categorySlug = deriveProductCategorySlug(product, this.rootsSig());
    if (!categorySlug) return;
    this.router.navigate(['/tienda', categorySlug, product.slug]);
  }

  private isSectionRendered(section: GameSection, id: string): boolean {
    switch (id) {
      case 'que-es':
        return section.intro?.whatIs != null;
      case 'como-jugar':
        return section.intro?.howToPlay != null;
      case 'por-donde-empezar':
        return section.starterGuide != null;
      case 'comunidad':
        return section.community != null;
      case 'blog':
        return section.blogPosts.length > 0;
      default:
        return false;
    }
  }

  /**
   * SEO con los datos del API, sobreescribiendo los defaults que el
   * `seoResolver` aplicó antes de activar el componente. Fallback: el copy que
   * el admin escribió para SEO, y si lo dejó vacío, el nombre del juego y su
   * lema, que es exactamente lo que la página muestra.
   */
  private applySeo(section: GameSection): void {
    this.seo.apply({
      title: section.metaTitle?.trim() || section.name,
      description: truncate(section.metaDescription?.trim() || section.tagline, 300),
      image: section.heroImageUrl || undefined,
      type: 'website',
      canonical: `/juegos/${section.slug}`,
    });
  }

  /** Una landing que no existe no debe indexarse con el título de la ruta. */
  private applyNotFoundSeo(): void {
    this.seo.apply({
      title: 'Juego no encontrado',
      robots: 'noindex,nofollow',
    });
  }

  private setupScrollSpy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.scrollSpyObserver?.disconnect();
    this.scrollSpyObserver = null;

    const elements = this.navSections()
      .map((nav) => document.getElementById(nav.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          this.activeSection.set(visible[0].target.id);
        }
      },
      {
        rootMargin: '-100px 0px -60% 0px',
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));
    this.scrollSpyObserver = observer;
  }
}
